/*
 * Theme preference — a control over a palette that was already designed.
 *
 * Both grounds have existed since the token file was written; what was missing was any way for a
 * person to choose. The palette followed `prefers-color-scheme` and nothing else, so a recruiter on
 * a dark laptop got the dark ground in a room where the light one reads better, and had no recourse.
 *
 * **The default stays `system`, deliberately.** The market review found light mode outperforms dark
 * on visual-acuity and proofreading tasks for normal-vision users, and that the advantage grows as
 * font size shrinks — which argues for defaulting a dense table to light. But `tokens.css` records
 * the opposite position in the design language: the dark ground is the primary artifact and the
 * light one its faithful counterpart. Reversing a documented decision as a side effect of adding a
 * switch is not this slice's call to make, and `system` changes nobody's experience while giving
 * everybody the control. If the default should become light, that is a design-language edit with an
 * argument attached.
 *
 * **The media query in `tokens.css` is kept**, so the dark ground still works with no JavaScript at
 * all. This module only ever *overrides* it, which is why the storage key can be absent, corrupt or
 * unreadable without the page losing its palette.
 */

import { useCallback, useEffect, useState } from 'react';
import { Select } from '@indovation/design-system';
import { useI18n } from '@indovation/i18n/react';

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'tf.theme';
const PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

function isPreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (PREFERENCES as readonly string[]).includes(value);
}

/**
 * Reads the stored preference, tolerating every way storage can fail.
 *
 * `localStorage` throws rather than returning null when a browser is configured to deny it — Safari
 * in private mode historically, and any locked-down enterprise profile. A theme preference is not
 * worth a blank screen.
 */
export function storedPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isPreference(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

/**
 * Stamps the root element.
 *
 * `system` *removes* the attribute rather than setting it to "system", which is what lets the
 * stylesheet's `prefers-color-scheme` query take over untouched. A third attribute value would mean
 * the CSS had to enumerate it, and the un-stamped state would still need handling anyway.
 */
export function applyPreference(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', preference);
  }
}

/** The control. Native `select` — three options do not justify a listbox. */
export function ThemeToggle() {
  const { t } = useI18n();
  const [preference, setPreference] = useState<ThemePreference>(storedPreference);

  // Applied on mount as well as on change: the inline script in `index.html` stamps before paint to
  // avoid a flash, but this is what keeps the DOM correct if that script was ever removed.
  useEffect(() => {
    applyPreference(preference);
  }, [preference]);

  const choose = useCallback((next: ThemePreference) => {
    setPreference(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A preference that cannot be persisted still applies for this session. Failing the change
      // because it cannot be remembered would be the worse outcome.
    }
  }, []);

  /*
   * `aria-label` on the control, **not** a wrapping `<label>` — and a browser test in an unrelated
   * flow is what forced this.
   *
   * A `<label>` that wraps a `<select>` has the select's options inside its text content, so this
   * control's label read "Colour theme" + "Match system" + "Light" + "Dark". Playwright's
   * `getByLabel` matches on that text, so `getByLabel('Match')` on the candidate search screen
   * suddenly resolved to two elements: the filter it meant, and a theme switcher in the header of
   * every screen in the product. One new control in the shell had made a substring label query
   * ambiguous everywhere.
   *
   * An explicit label on the control has neither problem: the accessible name is exactly the string
   * given, and no option text leaks into it.
   */
  return (
    <span className="tf-theme-toggle">
      <Select
        aria-label={t('theme.label')}
        value={preference}
        onChange={(event) => {
          const next = event.currentTarget.value;
          if (isPreference(next)) choose(next);
        }}
      >
        <option value="system">{t('theme.system')}</option>
        <option value="light">{t('theme.light')}</option>
        <option value="dark">{t('theme.dark')}</option>
      </Select>
    </span>
  );
}
